// Supply Network panel: filters row for the "App-using dealer
// locations" tab. Sister to ManufacturingPlantDealerLocationsFilters
// (non-app side). Same shape, but the linked-org filter is replaced
// with a subscriber-org filter because subscriber locations always
// belong to a subscriber dealer organization.
//
// Search lives in the wrapper (shared across both tabs); only the
// per-tab filters live here. Renders inline (no SectionCard).
import { mfgFilterGhostBtnClass, mfgXsInputTallClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  SUB_PLANT_FILTER_UNASSIGNED,
  type SubLocTypeFilter,
  type UseManufacturingSubscriberLocations,
} from "./useManufacturingSubscriberLocations";

// Slice 13b: location_type chips. "" = All; the other three map 1:1
// to public.locations.location_type values. "Online" is the user-
// facing label for the underlying `virtual` enum. Rows with
// location_type = "plant" or null only appear under "All".
const LOCATION_TYPE_CHIPS: { value: SubLocTypeFilter; label: string }[] = [
  { value: "", label: "All" },
  { value: "retail", label: "Retail" },
  { value: "virtual", label: "Online" },
  { value: "corporate", label: "Corporate" },
];

const chipBase =
  "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors";
const chipActive =
  "border-indigo-500 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-500";
const chipIdle =
  "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800";

export function ManufacturingPlantSubscriberLocationsFilters({
  m,
}: {
  m: UseManufacturingSubscriberLocations;
}) {
  const anyFilterActive =
    m.filterPlant !== "" ||
    m.filterSubscriberOrg !== "" ||
    m.filterState.trim().length > 0 ||
    m.filterActive !== "active" ||
    m.filterLocationType !== "";

  function resetFilters() {
    m.setFilterPlant("");
    m.setFilterSubscriberOrg("");
    m.setFilterState("");
    m.setFilterActive("active");
    m.setFilterLocationType("");
  }

  // Slice 10: filter row is now inline (no bordered card wrapper)
  // and uses gap-x-4 + flex-col labels so the label text has a
  // proper gap above its select and adjacent pairs aren't smashed
  // together.
  //
  // Slice 13b: chip row for location_type sits above the dropdown
  // row. Discrete enum, so chips read better than a 5th dropdown.
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 mr-1">
          Type
        </span>
        {LOCATION_TYPE_CHIPS.map((chip) => {
          const active = m.filterLocationType === chip.value;
          return (
            <button
              key={chip.value || "all"}
              type="button"
              className={`${chipBase} ${active ? chipActive : chipIdle}`}
              onClick={() => m.setFilterLocationType(chip.value)}
              aria-pressed={active}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
          Plant
          <select
            className={mfgXsInputTallClass}
            value={m.filterPlant}
            onChange={(e) => m.setFilterPlant(e.target.value)}
          >
            <option value="">Any plant</option>
            <option value={SUB_PLANT_FILTER_UNASSIGNED}>Unassigned (no plant)</option>
            {m.plants
              .filter((p) => p.is_active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code} · {p.name}
                </option>
              ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
          Subscriber org
          <select
            className={mfgXsInputTallClass}
            value={m.filterSubscriberOrg}
            onChange={(e) => m.setFilterSubscriberOrg(e.target.value)}
          >
            <option value="">Any subscriber</option>
            {m.knownSubscriberOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
          State
          <select
            className={mfgXsInputTallClass}
            value={m.filterState}
            onChange={(e) => m.setFilterState(e.target.value)}
          >
            <option value="">Any state</option>
            {m.knownStates.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
          Show
          <select
            className={mfgXsInputTallClass}
            value={m.filterActive}
            onChange={(e) =>
              m.setFilterActive(e.target.value === "all" ? "all" : "active")
            }
          >
            <option value="active">Active only</option>
            <option value="all">Show all (incl. inactive)</option>
          </select>
        </label>
        <div className="ml-auto">
          {anyFilterActive ? (
            <button type="button" className={mfgFilterGhostBtnClass} onClick={resetFilters}>
              Clear filters
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
